import { forwardRef, Ref, useImperativeHandle, useState } from "react";
import { Button } from "reactstrap";
import DisplayRecord from "./DisplayRecord";
import useKeyEvent from "../../hooks/useKeyEvent";
import AppDrawer from "../Drawers/AppDrawer";
import AppSpinner from "../../elements/AppSpinner";
import EmptyField from "./EmptyField";
import AppTooltip from "../../elements/AppTooltip";
import { Field } from "../../types/TenantInterfaces";
import StrUtils from "../../utils/StrUtils";
import { newToastEvent } from "../../utils/ToastUtil";
import RightArrow from "../../elements/RightArrow";
import JSONTextEditor from "../JSONTextEditor/JSONTextEditor";
import { AppConfirm } from "../../utils/PopupUtil";

interface Props {
  mode?: string;
  tableView?: boolean;
  recordID: string;
  modifyTrigger: string;
  field: Field;
  value: any;
  readonly?: boolean;
  onUpdate: (payload: any) => void;
  submitting: boolean;
}

const ObjectRecord = (
  {
    mode,
    tableView,
    recordID,
    modifyTrigger,
    field,
    value,
    readonly = false,
    onUpdate,
    submitting,
  }: Props,
  ref?: Ref<HTMLInputElement>
) => {
  const [newValue, setNewValue] = useState(null);
  const [resetCount, setResetCount] = useState(0);
  const [formatCount, setFormatCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tooltipId] = useState("tooltip" + StrUtils.uuidv4());

  const toggleEditing = () => setIsEditing((prev) => !prev);
  const handleObjectUpdate = (val: any) => setNewValue(val);
  const isValid = () => newValue && typeof newValue === "object";
  const formatJSON = () => setFormatCount((prev) => prev + 1);
  const isReadOnly = mode === "r";

  const openModal = (openInEditMode: boolean) => {
    if (openInEditMode) {
      setIsEditing(true);
    }

    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setIsEditing(false);
    setNewValue(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setResetCount((prev) => prev + 1);
  };

  const handleSave = () => {
    onUpdate({
      field,
      recordID,
      payload: { [field.APIName]: newValue },
      onSuccess: () => {
        closeModal();
        newToastEvent("success", `Successfully updated ${field.Name}.`);
      },
    });
  };

  const clearObject = async () => {
    const result = await AppConfirm({
      title: "Clear Object",
      text: "Are you sure you want to clear this object? This cannot be undone.",
      confirmButtonText: "Clear",
    });

    if (!result.isConfirmed) return;

    setNewValue(null);
    setIsEditing(false);

    onUpdate({
      field,
      recordID,
      payload: { [field.APIName]: null },
      onSuccess: () => {
        closeModal();
        newToastEvent("success", `Successfully cleared ${field.Name}.`);
      },
    });
  };

  useKeyEvent(closeModal);

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    focus: () => openModal(true),
  }));

  return (
    <>
      <DisplayRecord
        mode={"rw"} // We handle readonly access in the Drawer. we need to be able to open it!
        tableView={tableView}
        modifyTrigger={modifyTrigger}
        openModify={openModal}
        isSensitive={field?.Sensitive ?? false}
        displayComponent={() => {
          if (!value) return <EmptyField table={tableView} />;

          return (
            <>
              <AppTooltip
                useTooltip
                enterable={true}
                content={
                  <div className="p-2" style={{ maxWidth: 350 }}>
                    {JSON.stringify(value, null, "  ")}
                  </div>
                }
              >
                <div
                  className="text-primary-dark font-weight-500 cursor-pointer"
                  id={tooltipId}
                >
                  <>
                    View Object
                    <RightArrow />
                  </>
                </div>
              </AppTooltip>
            </>
          );
        }}
      />
      <AppDrawer
        open={isOpen}
        onClose={closeModal}
        headerTitle={field.Name}
        body={
          <>
            <JSONTextEditor
              readOnly={readonly ? readonly : !isEditing}
              existingObject={value}
              onChange={handleObjectUpdate}
              reset={resetCount}
              format={formatCount}
            />
            {!isReadOnly && isEditing ? (
              <div className="d-flex justify-content-between pt-3">
                <div>
                  <Button
                    onClick={formatJSON}
                    color="info"
                    disabled={!isValid() || submitting}
                  >
                    Format
                  </Button>
                </div>
                <div>
                  <Button onClick={cancelEditing}>Cancel</Button>
                  <Button
                    color="primary"
                    disabled={!isValid() || submitting}
                    onClick={handleSave}
                  >
                    {submitting ? <AppSpinner color="white" /> : "Save"}
                  </Button>
                </div>
              </div>
            ) : (
              !isReadOnly && (
                <div className="text-right pt-3">
                  {value && (
                    <Button onClick={clearObject} color="danger">
                      Clear
                    </Button>
                  )}
                  <Button
                    disabled={readonly}
                    onClick={toggleEditing}
                    color="primary"
                  >
                    Edit
                  </Button>
                </div>
              )
            )}
          </>
        }
      />
    </>
  );
};

export default forwardRef(ObjectRecord);
